import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SessionContext } from '../contexts/SessionContext';
import { useWebContainer } from '../contexts/WebContainerContext';
import { IFSWatcher, WebContainerProcess } from '@webcontainer/api';
import { useAsyncOperation } from '../hooks/useAsyncOperation';
import { MONITOR_SCRIPT } from '../core/script';
import ProcessOutput from '../core/ProcessOutput';

export interface SessionError {
  message: string;
  from?: string;
  type: 'terminal' | 'browser';
}

interface WebContainerProviderProps {
  sessionKey: string;
  initFiles: Array<{ name: string; content: string }>;
  buildCommand?: string;
  startCommand?: string;
  children: React.ReactNode;
  fileObservers?: Record<string, (content: string, pre?: string) => Promise<void>>;
  onError?: (error: SessionError) => void;
}

const convertFiles = (initFiles: Array<{ name: string; content: string }>) => {
  const files: any = {};

  initFiles.forEach(({ name, content }) => {
    const pathParts = name.split('/');
    let current = files;

    // 处理路径的每一部分
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      const isLastPart = i === pathParts.length - 1;

      if (isLastPart) {
        // 最后一部分是文件
        current[part] = {
          file: {
            contents: content,
          },
        };
      } else {
        // 中间部分是目录
        if (!current[part]) {
          current[part] = {
            directory: {},
          };
        }
        current = current[part].directory;
      }
    }
  });

  return files;
};

const SessionProvider: React.FC<WebContainerProviderProps> = ({
  sessionKey,
  initFiles: files,
  buildCommand = 'yarn',
  startCommand = 'yarn dev',
  children,
  fileObservers,
  onError,
}) => {
  const [loadingContent, setLoadingContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const { webContainer, iframeSrc } = useWebContainer();
  const currentProcessRef = useRef<WebContainerProcess | null>(null);
  const [startProcess, setStartProcess] = useState<WebContainerProcess | null>(null);
  const [writer, setWriter] = useState<WritableStreamDefaultWriter | null>(null);
  const [output, setOutput] = useState<ReadableStream | null>(null);
  const [processOutput, setProcessOutput] = useState<ProcessOutput | null>(null);
  const [sessionErrors, setSessionErrors] = useState<SessionError[]>([]);
  const watchersRef = useRef<IFSWatcher[]>([]);

  const clearErrors = useCallback(() => {
    setSessionErrors([]);
  }, []);

  useEffect(() => {
    if (!webContainer) return;
    clearErrors();
    webContainer.mount(convertFiles(files));
  }, [webContainer, files]);

  useEffect(() => {
    if (loading && iframeSrc) {
      setLoading(false);
    }
  }, [iframeSrc, loading]);

  const wrappedOnError = useCallback(
    (error: SessionError) => {
      setSessionErrors(prev => {
        if (
          !prev.find(item => item.message === error.message && item.from === error.from && item.type === error.type)
        ) {
          return [...prev, error];
        }
        return prev;
      });
      onError && onError(error);
    },
    [onError]
  );

  const initWebContainer = useCallback(async () => {
    if (!webContainer || !files || !files.length || !sessionKey) {
      return;
    }
    setLoadingContent('(1/4)初始化容器...');

    const finalFileObservers: Record<string, (content: string, pre?: string) => Promise<void>> = {
      ...fileObservers,
      'package.json': async () => {
        if (buildCommand) {
          const buildProcess = await webContainer.spawn(buildCommand);
          await buildProcess.exit;
        }
      },
    };

    const preContents: Record<string, string> = {};
    // 写入文件
    for (const file of files) {
      const { name, content } = file;
      setLoadingContent(`(2/4)写入文件${name}...`);
      console.log(`Writing file ${name}...`);
      if (name.indexOf('/') > -1) {
        const dir = name.substring(0, name.lastIndexOf('/'));
        await webContainer.fs.mkdir(dir, { recursive: true });
      }
      if (finalFileObservers && finalFileObservers[name]) {
        preContents[name] = content;
      }
      if (name === 'index.html') {
        let contentWithMonitor = content;
        if (content.indexOf('</body>') > -1) {
          contentWithMonitor = content.replace('</body>', `<script src="./error-monitor.js"></script></body>`);
        } else {
          contentWithMonitor = content + `<script src="./error-monitor.js"></script>`;
        }
        await webContainer.fs.writeFile(name, contentWithMonitor);
      } else {
        await webContainer.fs.writeFile(name, content);
      }
    }

    // 写入监控器
    await webContainer.fs.writeFile('error-monitor.js', MONITOR_SCRIPT);

    console.log('Writing files done');
    setLoadingContent('(3/4)构建中...');

    if (buildCommand) {
      const buildProcess = await webContainer.spawn(buildCommand);
      await buildProcess.exit;
    }

    if (finalFileObservers) {
      for (const key in finalFileObservers) {
        watchersRef.current.push(
          webContainer.fs.watch(key, async () => {
            const content = await webContainer.fs.readFile(key, 'utf-8');
            if (preContents[key] !== content) {
              finalFileObservers[key](content, preContents[key]);
              preContents[key] = content;
            }
          })
        );
        const content = await webContainer.fs.readFile(key, 'utf-8');
        if (preContents[key] !== content) {
          finalFileObservers[key](content, preContents[key]);
          preContents[key] = content;
        }
      }
    }

    console.log('Build done');

    const process = await webContainer.spawn('/bin/jsh', ['--osc'], {
      terminal: {
        cols: 60,
        rows: 24,
      },
    });
    currentProcessRef.current = process;
    setStartProcess(process);

    const writer = process.input.getWriter();
    setWriter(writer);
    const { output } = process;
    setOutput(output);
    setProcessOutput(new ProcessOutput(output, s => wrappedOnError({ type: 'terminal', message: s })));

    setLoadingContent('(4/4)启动中...');
    // 执行启动命令
    if (startCommand) {
      await writer.write(`${startCommand}\n`);
    }

    console.log('Start done');
  }, [sessionKey, webContainer]);

  const cleanup = useCallback(async () => {
    if (!webContainer) {
      return;
    }

    // 清理监听器
    watchersRef.current.forEach(watcher => {
      watcher.close();
    });
    watchersRef.current = [];

    // 清理进程
    if (currentProcessRef.current) {
      console.log('kill process');
      await currentProcessRef.current.kill();
      currentProcessRef.current = null;
    }

    await Promise.all([
      ...files.map(file => file.name).map(name => webContainer.fs.rm(name)),
      webContainer.fs.rm('yarn.lock'),
    ]);
    console.log('cleanup done');

    clearErrors();
  }, [webContainer]);

  const { execute } = useAsyncOperation(initWebContainer, cleanup);

  useEffect(() => {
    if (!webContainer) {
      return;
    }

    setLoading(true);
    execute();
  }, [webContainer, execute]);

  const fileMap = useMemo(() => {
    return files.reduce((pre, cur) => {
      pre[cur.name] = cur.content;
      return pre;
    }, {} as Record<string, string>);
  }, [files]);

  const setFile = useCallback(
    async (fileName: string, content: string) => {
      if (!webContainer) {
        return;
      }
      console.log(`Writing file ${fileName}...`);
      if (fileName.indexOf('/') > -1) {
        const dir = fileName.substring(0, fileName.lastIndexOf('/'));
        await webContainer.fs.mkdir(dir, { recursive: true });
      }
      await webContainer.fs.writeFile(fileName, content);
    },
    [webContainer]
  );

  const spawn = useCallback(
    async (command: string) => {
      if (!webContainer) {
        return;
      }
      await (
        await webContainer.spawn(command)
      ).exit;
    },
    [webContainer, writer]
  );

  return (
    <SessionContext.Provider
      value={{
        loading,
        loadingContent,
        startProcess,
        writer,
        output,
        processOutput,
        onError: wrappedOnError,
        sessionErrors,
        clearErrors,
        setFile,
        spawn,
        files: fileMap,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
